/**
 * ============================================================================
 * ReviewCalendar — Phase 7 frontend (Management Review Module).
 * Owner: Phase 7 — Management Review Module (Agent F).
 * Route: /management-reviews
 * Purpose: quarterly calendar of Management Reviews. Each card is a year-
 *          quarter bucket containing the review(s) scheduled for that
 *          quarter. Status badges call out where each review is in the
 *          FSM (SCHEDULED → HELD → MINUTES_FINALIZED → CLOSED). For HELD
 *          reviews, a strip of KPI mini-cards summarises the captured
 *          snapshot (CAPA / DEV / OOS / SUP / EQP / RISK).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state with primary CTA + ICH Q10 governance copy.
 *   - Loading state uses skeleton rows.
 *   - Cadence banner pinned at the top when MGR-001 status is WARNING_T_7
 *     or OVERDUE — surfaces the scheduler's verdict to the user.
 *
 * URS rows: MGR-001..006.
 * ============================================================================ */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ReviewCalendar.module.css';
import { StatusBadge } from './StatusBadge';
import { KpiMiniCard } from './KpiMiniCard';
import { ScheduleReviewDialog } from './ScheduleReviewDialog';
import {
  useReviewCalendarQuery,
  groupByQuarter,
  type ReviewListItem,
} from './useReviewCalendarQuery';

export interface ReviewCalendarProps {
  /** Optional host-shell callback when a row is clicked. */
  onOpenReview?: (id: string) => void;
  /** Optional cadence banner from a tenant-scoped scheduler probe. */
  cadenceStatus?: 'NO_PRIOR' | 'WITHIN_WINDOW' | 'WARNING_T_7' | 'OVERDUE';
  /** Optional days-until-due for the cadence banner. */
  cadenceDaysUntil?: number;
  /** Optional "Schedule new review" handler (host shell wires the dialog). */
  onScheduleNew?: () => void;
}

export function ReviewCalendar({
  onOpenReview,
  cadenceStatus,
  cadenceDaysUntil,
  onScheduleNew,
}: ReviewCalendarProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const q = useReviewCalendarQuery();
  const [showSchedule, setShowSchedule] = useState(false);

  // Always actionable: host may inject its own handler, otherwise open the
  // built-in schedule dialog (drafts/61 F0-8).
  const handleScheduleNew = onScheduleNew ?? (() => setShowSchedule(true));

  const items: ReviewListItem[] = q.data?.items ?? [];
  const byQuarter = useMemo(() => groupByQuarter(items), [items]);
  const quarterKeys = useMemo(
    () =>
      Object.keys(byQuarter).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0)),
    [byQuarter],
  );
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  function handleRowClick(r: ReviewListItem) {
    if (onOpenReview) {
      onOpenReview(r.id);
    } else {
      navigate({ to: '/management-reviews/$id', params: { id: r.id } });
    }
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('mgr.calendar.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('mgr.calendar.title', {
          defaultValue: 'Management review calendar',
        })}
        subtitle={t('mgr.calendar.subtitle', {
          defaultValue:
            'Quarterly cadence per ICH Q10 §II.B. Each review aggregates the eight quality KPIs at the moment it is held.',
        })}
        actions={
          <Button variant="primary" leadingIcon="+" onClick={handleScheduleNew}>
            {t('mgr.calendar.actions.schedule', {
              defaultValue: 'Schedule review',
            })}
          </Button>
        }
      />

      {cadenceStatus === 'WARNING_T_7' && (
        <div className={styles.cadenceBanner} role="status">
          {t('mgr.calendar.cadence.warning', {
            defaultValue:
              'Next quarterly review is due within 7 days — schedule it now to stay within the ICH Q10 cadence window.',
          })}
        </div>
      )}
      {cadenceStatus === 'OVERDUE' && (
        <div
          className={`${styles.cadenceBanner} ${styles.cadenceOverdue}`}
          role="alert"
        >
          {t('mgr.calendar.cadence.overdue', {
            defaultValue:
              'Quarterly review is OVERDUE — last review was more than 97 days ago. Schedule immediately.',
          })}
          {typeof cadenceDaysUntil === 'number' && (
            <span>
              {' '}
              ({Math.abs(cadenceDaysUntil)}{' '}
              {t('mgr.calendar.cadence.daysOverdue', { defaultValue: 'days overdue' })})
            </span>
          )}
        </div>
      )}

      {q.isLoading && (
        <Card>
          <CardBody>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={styles.skeletonRow} aria-hidden="true" />
            ))}
          </CardBody>
        </Card>
      )}

      {q.isError && (
        <Card>
          <CardBody>
            <p role="alert">
              {t('mgr.calendar.error', {
                defaultValue: 'Unable to load reviews. Please retry.',
              })}
            </p>
            <Button variant="secondary" onClick={() => q.refetch()}>
              {t('mgr.calendar.retry', { defaultValue: 'Retry' })}
            </Button>
          </CardBody>
        </Card>
      )}

      {!q.isLoading && !q.isError && items.length === 0 && (
        <div className={styles.emptyState}>
          <h2>
            {t('mgr.calendar.empty.title', {
              defaultValue: 'No management reviews scheduled yet',
            })}
          </h2>
          <p>
            {t('mgr.calendar.empty.body', {
              defaultValue:
                'ICH Q10 §II.B requires senior management to review process performance and product quality at a regular cadence (quarterly). Schedule the first review to begin the chain.',
            })}
          </p>
          <Button variant="primary" onClick={handleScheduleNew}>
            {t('mgr.calendar.empty.cta', { defaultValue: 'Schedule first review' })}
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <div className={styles.calendarGrid}>
          {quarterKeys.map((key) => (
            <Card key={key} className={styles.quarterCard}>
              <CardHeader>
                <div className={styles.quarterLabel}>{key}</div>
                <div className={styles.quarterTitle}>
                  {byQuarter[key]!.length}{' '}
                  {t('mgr.calendar.reviewCount', {
                    count: byQuarter[key]!.length,
                    defaultValue: 'reviews',
                  })}
                </div>
              </CardHeader>
              <CardBody>
                {byQuarter[key]!.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={styles.reviewRow}
                    onClick={() => handleRowClick(r)}
                  >
                    <div className={styles.reviewRowHeader}>
                      <span className={styles.reviewNumber}>{r.reviewNumber}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className={styles.reviewTitle}>{r.title}</div>
                    <div className={styles.reviewNumber}>
                      {t('mgr.calendar.scheduled', { defaultValue: 'Scheduled' })}:{' '}
                      {dateFmt.format(new Date(r.scheduledAtUtc))}
                      {r.heldAtUtc && (
                        <>
                          {' '}
                          ·{' '}
                          {t('mgr.calendar.held', { defaultValue: 'Held' })}:{' '}
                          {dateFmt.format(new Date(r.heldAtUtc))}
                        </>
                      )}
                    </div>
                    {r.status === 'HELD' ||
                    r.status === 'MINUTES_DRAFT' ||
                    r.status === 'MINUTES_FINALIZED' ||
                    r.status === 'CLOSED' ? (
                      <div className={styles.kpiTiles}>
                        <KpiMiniCard
                          label={t('mgr.kpi.attendees', { defaultValue: 'Attendees' })}
                          value={r.attendeeCount}
                        />
                        <KpiMiniCard
                          label={t('mgr.kpi.minutes', { defaultValue: 'Minutes' })}
                          value={r.minutesFinalized ? 'SIGNED' : 'PENDING'}
                        />
                      </div>
                    ) : null}
                  </button>
                ))}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {showSchedule && (
        <ScheduleReviewDialog
          onClose={() => setShowSchedule(false)}
          onCreated={() => {
            setShowSchedule(false);
            void q.refetch();
          }}
        />
      )}
    </div>
  );
}
